/**
 * Cliente HTTP mínimo. La clave pública y la identidad de sesión (`x-seats-session`, y `x-seats-session-token` si el
 * portal la emitió) solo viajan a la API (`/v1/*`), siempre en cabeceras, nunca en la URL: el plano y la foto de
 * disponibilidad son públicos (CDN) y se piden sin cabeceras personalizadas para evitar preflight CORS.
 */
import { parseSnapshot } from "./availability.js";
import type { EventConfig, HoldApi, HoldView, PlaceRequest, Snapshot } from "./types.js";

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status = 0,
    readonly details?: unknown,
    readonly retryable = false,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Errores definitivos: reintentar no sirve. La cuota se libera al soltar, confirmar o vencer un bloqueo, y una sesión
 * ausente o caducada solo la arregla el portal. Nunca son transitorios, diga lo que diga el servidor.
 */
export const FINAL_CODES: ReadonlySet<string> = new Set(["quota_exceeded", "session_required", "session_invalid"]);

export interface ApiConfig {
  baseUrl: string;
  key: string;
  /** id de sesión anónima (`x-seats-session`); vacío = no se envía */
  sessionId?: string;
  /** token de sesión del portal (`x-seats-session-token`); se lee en cada petición para poder renovarlo */
  sessionToken?: () => string | undefined;
  timeoutMs?: number;
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
}

/** Cabeceras de una llamada a `/v1/*`. El token viaja solo aquí: no se interpola en URLs, mensajes ni errores. */
export function apiHeaders(key: string, sessionId: string | undefined, sessionToken: string | undefined, json: boolean): Record<string, string> {
  const h: Record<string, string> = { authorization: `Bearer ${key}` };
  if (sessionId) h["x-seats-session"] = sessionId;
  if (sessionToken) h["x-seats-session-token"] = sessionToken;
  if (json) h["content-type"] = "application/json";
  return h;
}

export const joinUrl = (base: string, path: string): string => (/^https?:\/\//i.test(path) ? path : base.replace(/\/+$/, "") + (path.startsWith("/") ? path : `/${path}`));

export function createApi(cfg: ApiConfig) {
  const doFetch = cfg.fetchImpl ?? ((i: RequestInfo | URL, o?: RequestInit) => fetch(i, o));
  const timeout = cfg.timeoutMs ?? 12_000;

  async function send(url: string, init: RequestInit): Promise<{ res: Response; json: unknown }> {
    const ctrl = new AbortController();
    const onAbort = () => ctrl.abort();
    cfg.signal?.addEventListener("abort", onAbort, { once: true });
    const timer = setTimeout(() => ctrl.abort(), timeout);
    try {
      const res = await doFetch(url, { ...init, signal: ctrl.signal, credentials: "omit" });
      const text = await res.text();
      let json: unknown = null;
      if (text) {
        try {
          json = JSON.parse(text);
        } catch {
          json = null;
        }
      }
      return { res, json };
    } catch (e) {
      if (cfg.signal?.aborted) throw new ApiError("aborted", "Cancelado", 0, undefined, false);
      const aborted = e instanceof Error && e.name === "AbortError";
      throw new ApiError(aborted ? "timeout" : "network", aborted ? "La petición tardó demasiado" : "Error de red", 0, undefined, true);
    } finally {
      clearTimeout(timer);
      cfg.signal?.removeEventListener("abort", onAbort);
    }
  }

  function fail(res: Response, json: unknown): never {
    const err = (json as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    const details = err?.details;
    const retryable = !FINAL_CODES.has(err?.code ?? "") && (res.status >= 500 || err?.code === "busy" || (details as { retryable?: boolean } | undefined)?.retryable === true);
    throw new ApiError(err?.code ?? `http_${res.status}`, err?.message ?? res.statusText ?? "Error", res.status, details, retryable);
  }

  async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const r = await send(joinUrl(cfg.baseUrl, path), {
      method,
      headers: apiHeaders(cfg.key, cfg.sessionId, cfg.sessionToken?.(), body !== undefined),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      cache: "no-store",
    });
    if (!r.res.ok) fail(r.res, r.json);
    return r.json as T;
  }

  async function getPublic<T>(path: string, cache: RequestCache): Promise<T> {
    const r = await send(joinUrl(cfg.baseUrl, path), { method: "GET", cache });
    if (!r.res.ok) fail(r.res, r.json);
    if (!r.json || typeof r.json !== "object") throw new ApiError("bad_response", "Respuesta no válida", r.res.status, undefined, true);
    return r.json as T;
  }

  const enc = encodeURIComponent;

  return {
    event: (eventId: string) => call<EventConfig>("GET", `/v1/events/${enc(eventId)}`),
    /** El plano es inmutable (el hash va en la ruta): se deja trabajar a la caché del navegador. */
    chart: (url: string) => getPublic<unknown>(url, "default"),
    /**
     * `no-cache` revalida siempre: con `max-age=2, stale-while-revalidate=5` el navegador serviría fotos de hasta
     * ~7 s. `fresh` (tras un conflicto) se salta además la caché del navegador.
     *
     * Devuelve null si la foto todavía no existe: un evento sin cambios no la tiene y la CDN responde 403/404 con un
     * cuerpo XML. No es un error: significa "todo libre". Solo los fallos de red y los 5xx se propagan como error.
     */
    async snapshot(url: string, fresh = false): Promise<Snapshot | null> {
      const r = await send(joinUrl(cfg.baseUrl, url), { method: "GET", cache: fresh ? "reload" : "no-cache" });
      if (r.res.status >= 500) fail(r.res, r.json);
      if (!r.res.ok) return null;
      return parseSnapshot(r.json);
    },
    /** API de bloqueos de un evento concreto. */
    holds(eventId: string): HoldApi {
      return {
        place: (req: PlaceRequest) => call<HoldView>("POST", `/v1/events/${enc(eventId)}/holds`, req),
        remove: (token, seats) => call<HoldView>("POST", `/v1/holds/${enc(token)}/remove`, { seats }),
        release: (token) => call<unknown>("DELETE", `/v1/holds/${enc(token)}`),
        extend: (token) => call<HoldView>("POST", `/v1/holds/${enc(token)}/extend`),
        get: (token) => call<HoldView>("GET", `/v1/holds/${enc(token)}`),
      };
    },
  };
}

export type Api = ReturnType<typeof createApi>;
